import { Children, HTMLProps, ReactNode } from 'react';
import styled, { css, ThemeProps } from 'styled-components';
import { Theme } from '../theme';
import { font, media } from '../theme/base/mixins';
import {
  StyledDivComponent,
  StyledInputComponent,
  StyledLabelComponent,
  StyledSpanComponent
} from '../types';
import { iconMapping } from './FontIcon';
import { Row } from './Grid';
import { Icon } from './Icon';
import { Image } from './Image';
// import { ReactComponentStyled } from 'styled-components';
// import { ComponentType } from 'react';
/**
 * props:
 * - inlined -> for having inline label and Input Item
 * - inverted -> to invert background and foreground color
 */

export const Form = styled.form`
  width: 100%;
`;

interface InputProps {
  backgroundcolor?: string;
  inverted?: boolean;
  inlined?: boolean;
  small?: boolean;
  error?: boolean;
}

type InputPropsFull = ThemeProps<Theme> &
  InputProps &
  HTMLProps<HTMLInputElement>;

const BaseInput: StyledInputComponent<InputProps> = styled.input`
  /* clears the 'X' from Internet Explorer */
  ::-ms-clear,
  ::-ms-reveal {
    display: none !important;
    width: 0;
    height: 0;
  }
`;

export const Input: StyledInputComponent<InputProps> = styled(BaseInput).attrs({
  backgroundcolor: ({ inverted, theme }: InputPropsFull) =>
    inverted ? theme.colors.white : theme.colors.grayLight
})`
  font-size: ${(props: InputPropsFull) => (props.small ? '1.6' : '2.2')}rem;
  height: ${(props: InputPropsFull) => (props.small ? '36px' : 'auto')};

  line-height: 1.5;
  width: ${(props: InputPropsFull) => (props.inlined ? 'auto' : '100%')};
  padding: ${(props: InputPropsFull) =>
    props.small ? '5px 10px' : '14px 29px'};
  border: 1px solid
    ${({ error, theme, backgroundcolor }: InputPropsFull) =>
      error ? theme.colors.error : backgroundcolor};
  border-radius: 0;
  background-color: ${({ backgroundcolor }: InputPropsFull) => backgroundcolor};
  outline: none;
  ${(props: InputPropsFull) => (props.inlined ? 'display: inline-block' : '')};
  font-family: inherit;
  font-weight: 500;
  box-sizing: border-box;

  ${(props: InputPropsFull) =>
    (props.type === 'radio' || props.type === 'checkbox') &&
    css`
      appearance: none;
      width: auto;
      background: transparent;
      padding: 0;
      margin: 0;
      position: absolute;
      opacity: 0;
      cursor: pointer;
    `};

  :checked + span {
    position: relative;

    &:before {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    ${(props: InputPropsFull) =>
      props.type === 'radio' &&
      css`
        &:before {
          content: '';
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: ${props.theme.colors.primary};
        }
      `}

    ${(props: InputPropsFull) =>
      props.type === 'checkbox' &&
      css`
        background-color: ${props.theme.colors.primary};

        &:before {
          font-family: 'busstore-icons';
          font-style: normal !important;
          font-weight: normal !important;
          text-decoration: inherit;
          text-rendering: auto;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;

          content: "${iconMapping.check}";
          color: ${props.theme.colors.white};
        }
      `};
  }
`;

export const Textarea = Input.withComponent('textarea');

interface CheckIconProps {
  error?: boolean;
  type?: 'klas';
}

type CheckIconPropsThemed = ThemeProps<Theme> & CheckIconProps;

export const CheckIcon = styled.span<CheckIconProps>`
  position: relative;
  display: block;
  width: ${(props) => props.type === 'klas' ? '24px' : '28px'};
  height: ${(props) => props.type === 'klas' ? '24px' : '28px'};
  margin-right: 10px;
  cursor: pointer;
  background-color: ${(props: CheckIconPropsThemed) =>
    props.theme.colors.white};
  border: ${(props: CheckIconPropsThemed) => props.theme.defaults.borderSize}
    ${(props: CheckIconPropsThemed) => props.theme.defaults.borderStyle}
    ${({ error, theme }: CheckIconPropsThemed) =>
      error ? theme.colors.error : theme.colors.primary};
  transition: ${(props: CheckIconPropsThemed) =>
    props.theme.defaults.transition};
  flex: 0 0 28px;

  ${(props: CheckIconPropsThemed) =>
    props.type === 'klas' &&
    css`
      margin-top: 20px;
      margin-left: 36px;
    `};

  ${Icon} {
    display: none;
    line-height: 0;
    position: absolute;

    svg {
      fill: ${(props: CheckIconPropsThemed) => props.theme.colors.white};
    }
  }
`;

export const RadioIcon = CheckIcon.extend`
  border-radius: 50%;
`;

export interface SelectProps {
  backgroundcolor?: string;
  inverted?: boolean;
  ghost?: boolean;
  small?: boolean;
  error?: boolean;
  borderless?: boolean;
}

type SelectPropsThemed = ThemeProps<Theme> & SelectProps;

const BaseSelect: StyledDivComponent<SelectProps> = styled.div``;

export const Select: StyledDivComponent<SelectProps> = styled(BaseSelect).attrs(
  {
    backgroundcolor: ({ inverted, ghost, theme }: SelectPropsThemed) =>
      inverted
        ? theme.colors.white
        : ghost
        ? 'transparent'
        : theme.colors.grayLight
  }
)`
  position: relative;

  select {
    pointer-events: all;
    font-size: ${(props: SelectPropsThemed) =>
      props.small ? '1.6' : '2.2'}rem;
    height: ${(props: SelectPropsThemed) => (props.small ? '36px' : 'auto')};
    line-height: 1.5;
    display: block;
    width: 100%;
    padding: ${(props: SelectPropsThemed) =>
      props.small ? '5px 29px 5px 10px' : '14px 29px'};
    appearance: none;
    background: ${({ backgroundcolor }: SelectPropsThemed) => backgroundcolor};
    border: 1px solid
      ${({ ghost, error, theme, backgroundcolor }: SelectPropsThemed) =>
        error
          ? theme.colors.error
          : ghost
          ? theme.colors.black
          : backgroundcolor};
    ${(props) => props.borderless ? 'border: none;' : ''}
    box-shadow: none;
    border-radius: 0;
    outline: none;
    font-family: inherit;
    font-weight: 500;
    cursor: pointer;
    color: ${(props: SelectPropsThemed) => props.theme.colors.black};

    /* IE11 - fix for dropdown arrow */
    &::-ms-expand {
      display: none;
    }
  }

  ${Icon} {
    display: inline;
    margin: 0;
    position: absolute;
    top: 50%;
    right: 13px;
    width: 15px;
    height: auto;
    transform: translateY(-50%);
    pointer-events: none;

    svg {
      fill: ${(props: SelectPropsThemed) => props.theme.colors.primary};
    }
  }
`;

interface LabelProps {
  inlined?: boolean;
  checkbox?: boolean;
  radio?: boolean;
  children?: ReactNode;
  margin?: boolean;
}

type LabelPropsThemed = ThemeProps<Theme> & LabelProps;

export const Label: StyledLabelComponent<LabelProps> = styled.label`
  display: ${(props: LabelPropsThemed) =>
    props.inlined ? 'inline-block' : 'block'};
  color: ${(props: LabelPropsThemed) => props.theme.colors.secondary};

  ${font()};

  ${(props: LabelPropsThemed) =>
    Children.count(props.children) > 1 &&
    css`
      margin-bottom: 20px;
    `};

  ${(props: LabelPropsThemed) =>
    props.margin === false &&
    css`
      margin-bottom: 0;
    `};

  ${Image} {
    width: 52px;
    position: absolute;
  }

  ${(props: LabelPropsThemed) =>
    (props.checkbox || props.radio) &&
    css`
      display: flex;
      align-items: center;
    `};
`;

interface InlinedSmallLabelProps {
  required?: boolean;
  error?: boolean;
}

type InlinedSmallLabelPropsThemed = ThemeProps<Theme> & InlinedSmallLabelProps;

export const InlinedSmallLabel: StyledSpanComponent<
  InlinedSmallLabelProps
> = styled.span`
  display: inline-block;
  position: relative;
  flex: 1;
  font-size: 1.6rem;
  line-height: 1.25;

  ${(props: InlinedSmallLabelPropsThemed) =>
    props.required &&
    css`
      &:after {
        content: '*';
      }
    `}

  ${({ error, theme }: InlinedSmallLabelPropsThemed) =>
    error
      ? css`
          color: ${theme.colors.error};
        `
      : ''};
`;

export const InputGroup = Row.extend.attrs({
  isCollapsed: true
})`
  > *:not(:first-child) {
    border-left: ${props => css`2px solid ${props.theme.colors.gray}`};
  }

  > *:not(:last-child) {
    border-right: ${props => css`2px solid ${props.theme.colors.gray}`};
  }

  ${Select} {
    height: 100%;
  }
`;

export const InputGroupWrapper = styled.div`
  margin-bottom: 20px;
`;

export const DataProtectionText = styled.p`
  font-size: 1.6rem;
  line-height: 1.25;
  margin-bottom: 20px;

  * {
    font-weight: inherit;
    font-size: 1.6rem;
    line-height: 1.25;
  }

  a {
    text-decoration: underline;
  }
`;

export const FormInfoText = styled.p`
  font-size: 1.6rem;
  line-height: 1.25;
  margin-bottom: 20px;
`;

interface LabelSpanProps {
  required?: boolean;
}

export const LabelSpan: StyledSpanComponent<LabelSpanProps> = styled.span`
  margin-bottom: 3px;
  display: inline-block;

  ${(props: LabelSpanProps) =>
    props.required &&
    css`
      &:after {
        content: '*';
      }
    `};
`;

export const RequiredInfoText: StyledSpanComponent = styled.span`
  display: inline-block;
  margin-bottom: 20px;
  font-size: 1.4rem;
  color: ${(props: ThemeProps<Theme>) => props.theme.colors.grayDark};

  &:before {
    content: '*';
  }
`;
